import React from "react";
import { Badge } from "@/components/ui/badge";
import { Crosshair, Swords } from "lucide-react";
import { useNav, VIEWS } from "@/lib/nav";

function StatStrip({ stats, testId, size = "md" }) {
  const entries = Object.entries(stats || {});
  if (!entries.length) return null;
  const val = size === "lg" ? "text-xl" : "text-base";
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1" data-testid={testId}>
      {entries.map(([k, v]) => (
        <div key={k} className="text-center leading-tight min-w-[28px]">
          <div className="text-[10px] tracking-wide text-muted-foreground">{k}</div>
          <div className={`${val} font-semibold font-mono`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function WeaponBlock({ weapon, testId }) {
  const { navigate } = useNav();
  const ranged = weapon.type.toLowerCase() === "ranged";
  const Icon = ranged ? Crosshair : Swords;
  return (
    <div
      className={`rounded-md border p-3 ${ranged ? "border-emerald-800/60 bg-emerald-950/25" : "border-amber-800/60 bg-amber-950/25"}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${ranged ? "text-emerald-300" : "text-amber-300"}`} />
            <span className="font-semibold">{weapon.name}</span>
            {weapon.count > 1 && <Badge variant="outline" className="text-[10px]">×{weapon.count}</Badge>}
            {weapon.location && <span className="text-[11px] text-muted-foreground">{weapon.location}</span>}
          </div>
          {weapon.qualities.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {weapon.qualities.map((q) => (
                <Badge key={q} variant="outline" className="text-[10px] text-muted-foreground">{q}</Badge>
              ))}
            </div>
          )}
        </div>
        <StatStrip stats={weapon.stats} testId={`${testId}-stats`} />
      </div>
      {weapon.abilities.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/60 text-sm flex flex-wrap gap-x-1">
          {weapon.abilities.map((a, i) => (
            <button
              key={a.id}
              onClick={() => navigate(VIEWS.rule(a.id))}
              className="hover:text-primary hover:underline"
              data-testid={`${testId}-ability-${a.id}`}
            >
              {a.name}{i < weapon.abilities.length - 1 ? "," : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** In-game style model block: stat line, base, advantages, weapon profiles. */
export function ModelProfile({ model, showName, index }) {
  const testId = `model-profile-${index}`;
  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          {showName && <h4 className="font-semibold mb-1">{model.name}</h4>}
          <div className="flex flex-wrap gap-1">
            {model.base_size && <Badge variant="outline" className="text-[10px] text-cyan-300 border-cyan-800/60">{model.base_size} base</Badge>}
            {model.advantages.map((a) => (
              <Badge key={a} variant="outline" className="text-[10px]" data-testid={`${testId}-advantage`}>{a}</Badge>
            ))}
          </div>
        </div>
        <StatStrip stats={model.stats} testId={`${testId}-stats`} size="lg" />
      </div>
      {model.weapons.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Weapons</p>
          {model.weapons.map((w, i) => (
            <WeaponBlock key={w.id} weapon={w} testId={`${testId}-weapon-${i}`} />
          ))}
        </div>
      )}
    </div>
  );
}
